import { useState } from "react";
import { DateTime } from "luxon";
import { useHistory, useParams } from "react-router-dom";
import { ScorePage } from "src/pages/authenticated/ScoreTemplates";
import { Breakpoints, Button, Card, Checkbox, fontSmall, fontXSmall } from "swing-components";

import { AlertError, ContentSingleColumn } from "~components";
import { createTimesheetUrl } from "~pages";
import { translate } from "~utils";

import { TimecardEntryForm, TimeInterval } from "../components/TimecardEntryForm";
import { useTimesheet } from "../hooks/useTimesheet";
import { TimesheetEntry } from "../TimesheetRecord";
import { extractBreakPeriodsFromPunches } from "../utils";

/***** Types *****/

/** Form data structure for timecard entry editing */
type TimecardEntryFormData = {
  timeInterval?: TimeInterval;
  mealBreaks: TimeInterval[];
  durationInMinutes?: number;
};

/** Props for the TimecardEntryEditView component */
type TimecardEntryEditProps = {
  timesheet?: TimesheetEntry;
  error?: Error | null;
  isLoading?: boolean;
  intervalId?: string;
  indexId?: string;
  onEntrySubmit?: () => void;
};

/** Route parameters for the timecard entry edit page */
type TimecardEntryEditParams = {
  intervalId: string;
  indexId: string;
};

/***** Helper Functions *****/

/**
 * Extracts work times and break periods from punch sequences using the shared utility function
 * @param punches - Array of punch entries
 * @returns Object containing work start/end times and meal breaks
 */
function extractWorkAndBreakTimes(punches: any[]): {
  workStart: DateTime | undefined;
  workEnd: DateTime | undefined;
  mealBreaks: TimeInterval[];
} {
  if (!punches || punches.length === 0) {
    return {
      workStart: undefined,
      workEnd: undefined,
      mealBreaks: [],
    };
  }

  // Convert to the format expected by the shared utility function
  const formattedPunches = punches.map((punch) => ({
    type: punch.type,
    ts: punch.ts, // Already in string format, shared function handles this
    id: punch.id,
  }));

  // Use the shared utility function
  const { workPeriod, breakPeriods } = extractBreakPeriodsFromPunches(formattedPunches);

  // Convert break periods to TimeInterval format
  const mealBreaks: TimeInterval[] = breakPeriods.map((breakPeriod) => ({
    startTime: breakPeriod.start,
    endTime: breakPeriod.end,
  }));

  return {
    workStart: workPeriod.start,
    workEnd: workPeriod.end,
    mealBreaks,
  };
}

/***** Constants *****/

const FORM_VALIDATION_MESSAGES = {
  CERTIFICATION_REQUIRED: "TIMECARD_ENTRY_CERTIFICATION_CHECKBOX_LABEL",
  SUBMIT_ENTRY: "TIMESHEET_SUBMIT_ENTRY_LABEL",
} as const;

const PAYCODE_DISPLAY_LABELS: Record<string, string> = {
  "CA Sick": "Sick Leave",
  NJSIC: "Sick Leave",
  "Mandated Reporter Training": "Mandated Reporter Training",
  "Admin New Hire Paperwork": "New Hire Paperwork",
} as const;

const LAYOUT_SPACING = {
  CARD_GAP: 24,
  MOBILE_PADDING: 16,
  DESKTOP_PADDING: 0,
} as const;

/***** Main Components *****/

/**
 * Container component that handles data fetching and routing for timecard entry editing
 *
 * TODO: Rename to `TimesheetDetailsPage` with filename of `TimesheetDetails.page.tsx`
 */
export function TimecardEntryEdit() {
  const params = useParams<TimecardEntryEditParams>();

  const { data: timesheets, error, isLoading } = useTimesheet(params.intervalId);

  // Extract the specific entry using indexId
  const entryIndex = params.indexId ? parseInt(params.indexId, 10) : -1;
  const timesheet =
    timesheets && entryIndex >= 0 && entryIndex < timesheets.length
      ? timesheets[entryIndex]
      : undefined;

  return (
    <TimecardEntryEditView
      timesheet={timesheet}
      error={error}
      isLoading={isLoading}
      intervalId={params.intervalId}
      indexId={params.indexId}
    />
  );
}

/**
 * View component for editing timecard entries with form validation and submission
 * @param props - Component props containing timesheet data and handlers
 * @returns JSX element for the timecard entry edit view
 */
export function TimecardEntryEditView(props: TimecardEntryEditProps) {
  const { timesheet, error: queryError, isLoading, onEntrySubmit } = props;
  const history = useHistory();

  /********* State Management ********/

  const [isCertified, setIsCertified] = useState(false);

  // Initialize form data based on existing timesheet entry
  const [formData, setFormData] = useState<TimecardEntryFormData>(() => {
    if (timesheet) {
      return transformTimesheetEntryToFormData(timesheet);
    }
    return {
      timeInterval: undefined,
      mealBreaks: [],
    };
  });

  /********* Event Handlers ********/

  /**
   * Handles form data changes from the TimecardEntryForm component
   * Converts between different form formats (punches vs paycode)
   * @param update - The form update data
   */
  function handleFormDataChange(update: any) {
    // Convert the TimecardEntryForm format to our internal format
    if ("punches" in update && update.punches) {
      // Handle Timecard updates using proper punch sequence analysis
      const { workStart, workEnd, mealBreaks } = extractWorkAndBreakTimes(update.punches);

      setFormData({
        timeInterval: {
          startTime: workStart,
          endTime: workEnd,
        },
        mealBreaks,
      });
    } else if ("durationInMinutes" in update) {
      // Handle Paycode updates with duration
      setFormData({
        timeInterval: undefined, // Clear time interval for duration-based entries
        mealBreaks: [],
        durationInMinutes: update.durationInMinutes,
      });
    }
  }

  /**
   * Handles form submission by preparing data and navigating back to timesheet list
   * TODO: Replace console.log with actual API submission
   */
  function handleSubmit() {
    const submitData = {
      timesheetId: timesheet?.type === "TIMECARD" ? timesheet.request.id : timesheet?.id,
      date: timesheet?.type === "TIMECARD" ? timesheet.date : DateTime.now().toISODate(),
      school: timesheet?.type === "TIMECARD" ? timesheet.school : undefined,
      timeInterval: formData.timeInterval,
      mealBreaks: formData.mealBreaks,
      durationInMinutes: formData.durationInMinutes,
      isCertified,
    };

    console.log("Submitting timecard entry:", submitData);
    // TODO: Update to submit to API when ready

    // Call the parent handler to refresh data
    onEntrySubmit?.();

    // Navigate back to timesheet list
    history.push(createTimesheetUrl());
  }

  /********* Computed Values ********/

  const isFormValid = validateFormData(formData, isCertified);
  const pageTitle = formatPageTitle(timesheet);

  /********* Render ********/

  return (
    <ScorePage title={pageTitle} isLoading={isLoading} hasBack>
      <ContentSingleColumn>
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: LAYOUT_SPACING.CARD_GAP,
            padding: LAYOUT_SPACING.MOBILE_PADDING,
            [Breakpoints.mediumDeviceAndUp]: { padding: LAYOUT_SPACING.DESKTOP_PADDING },
          }}
        >
          {/* Error Display */}
          {/* TODO: Update to show mutation errors as well */}
          {queryError && <AlertError />}

          {/* Timecard Request Details */}
          {timesheet && timesheet.type === "TIMECARD" && (
            <Card>
              <p css={{ margin: 0 }}>{timesheet.school.name}</p>
              <p css={{ margin: 0, ...fontSmall({ fontWeight: 600 }), lineHeight: "24px" }}>
                {timesheet.date.toFormat("ccc, LLL d")}
              </p>
              <p css={{ margin: 0, ...fontXSmall() }}>
                {timesheet.request.startTime.toFormat("h:mm a").toLowerCase()}
                {" - "}
                {timesheet.request.endTime.toFormat("h:mm a").toLowerCase()}
              </p>
            </Card>
          )}

          {/* Paycode Entry Details */}
          {timesheet && timesheet.type === "PAYCODE" && (
            <Card>
              <p css={{ margin: 0 }}>{getPaycodeDisplayLabel(timesheet.payCode || "")}</p>
              <p css={{ margin: 0, ...fontSmall({ fontWeight: 600 }), lineHeight: "24px" }}>
                {timesheet.date ? timesheet.date.toFormat("ccc, LLL d") : ""}
              </p>
            </Card>
          )}

          {/* Entry Form */}
          <TimecardEntryForm
            entryType={timesheet?.type === "TIMECARD" ? "Timecard" : "Paycode"}
            timecardPunches={
              timesheet?.type === "TIMECARD"
                ? timesheet.entries.map((entry) => ({
                    id: entry.id || "",
                    ts: entry.ts?.toISO() || "",
                    // Map string literals to consistent punch type values
                    type: entry.type === "PUNCH_IN" ? "PUNCH_IN" : "PUNCH_OUT",
                  }))
                : undefined
            }
            paycodeDurationInMinutes={
              timesheet?.type === "PAYCODE" ? timesheet.durationInMinutes : undefined
            }
            onChange={handleFormDataChange}
          />

          {/* Certification Checkbox */}
          <Card>
            <div css={{ marginLeft: -16, marginRight: -16 }}>
              <Checkbox
                label={translate(FORM_VALIDATION_MESSAGES.CERTIFICATION_REQUIRED)}
                isChecked={isCertified}
                onCheckedChange={setIsCertified}
              />
            </div>
          </Card>

          {/* Submit Button */}
          <div
            css={{
              display: "flex",
              justifyContent: "center",
              [Breakpoints.mediumDeviceAndUp]: {
                justifyContent: "flex-end",
              },
            }}
          >
            <Button
              type="primary"
              onClick={handleSubmit}
              isFluid={!matchMedia(Breakpoints.mediumDeviceAndUp.replace("@media ", "")).matches}
              status={!isFormValid ? "disabled" : "enabled"}
            >
              {translate(FORM_VALIDATION_MESSAGES.SUBMIT_ENTRY)}
            </Button>
          </div>
        </div>
      </ContentSingleColumn>
    </ScorePage>
  );
}

/***** Utility Functions *****/

/**
 * Gets the display label for a paycode, falling back to the paycode name if not found
 * @param paycodeKey - The paycode key to look up
 * @returns The display label or the original paycode key if not mapped
 */
function getPaycodeDisplayLabel(paycodeKey: string): string {
  return PAYCODE_DISPLAY_LABELS[paycodeKey] || paycodeKey;
}

/**
 * Transforms a TimesheetEntry to form data format suitable for editing
 * @param entry - The timesheet entry to transform
 * @returns Form data with time intervals and meal breaks
 */
function transformTimesheetEntryToFormData(entry: TimesheetEntry): TimecardEntryFormData {
  if (entry.type === "TIMECARD") {
    // Convert timecard entries to the format expected by the shared utility function
    const punches = entry.entries
      .filter((e) => e.ts && e.type) // Only include entries with timestamps and valid types
      .map((e) => ({
        type: e.type as "PUNCH_IN" | "PUNCH_OUT",
        ts: e.ts as DateTime,
        id: e.id,
      }));

    // Use the shared utility function
    const { workPeriod, breakPeriods } = extractBreakPeriodsFromPunches(punches);

    // Convert break periods to TimeInterval format
    const mealBreaks: TimeInterval[] = breakPeriods.map((breakPeriod) => ({
      startTime: breakPeriod.start,
      endTime: breakPeriod.end,
    }));

    return {
      timeInterval: {
        startTime: workPeriod.start,
        endTime: workPeriod.end,
      },
      mealBreaks,
    };
  } else {
    // Handle PayCode entries - use duration-based approach
    return {
      timeInterval: undefined, // Clear time interval for duration-based entries
      mealBreaks: [],
      durationInMinutes: entry.durationInMinutes,
    };
  }
}

/**
 * Validates if the form data is complete and ready for submission
 * @param formData - The form data to validate
 * @param isCertified - Whether the user has certified the entry
 * @returns True if form is valid for submission
 */
function validateFormData(formData: TimecardEntryFormData, isCertified: boolean): boolean {
  const hasValidTimeInterval = formData.timeInterval?.startTime && formData.timeInterval?.endTime;
  const hasValidDuration =
    formData.durationInMinutes !== undefined && formData.durationInMinutes > 0;

  return Boolean((hasValidTimeInterval || hasValidDuration) && isCertified);
}

/**
 * Formats the page title based on timesheet entry type and date
 * @param timesheet - The timesheet entry
 * @returns Formatted title string
 */
function formatPageTitle(timesheet?: TimesheetEntry): string {
  if (!timesheet?.date) return "";

  return timesheet.date.toFormat("ccc, LLL d");
}
